import React from "react";
import {
  BankOutlined,
  BarChartOutlined,
  TeamOutlined,
  StockOutlined,
} from "@ant-design/icons";
import { Tabs, Card, Skeleton } from "antd";
import type { TabsProps } from "antd";
import {
  CompanyProfileTab,
  FinancialsTab,
  PeersTab,
  ChartsTab,
} from "./TabsContents";

interface StockAnalysisTabsProps {
  stockLoading: boolean;
  activeTab: string;
  onTabChange: (key: string) => void;
  isPremiumUser: boolean;
  selectedStock: any;
  sectorPeers: any[];
  baseCurrency: string;
}

const StockAnalysisTabs: React.FC<StockAnalysisTabsProps> = ({
  stockLoading,
  activeTab,
  onTabChange,
  isPremiumUser,
  selectedStock,
  sectorPeers,
  baseCurrency,
}) => {
  const tabConfigs = [
    {
      key: "company_profile",
      label: "Company Profile",
      icon: BankOutlined,
      children: stockLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <CompanyProfileTab selectedStock={selectedStock} />
      ),
    },
    ...(selectedStock?.short_name === "CSE"
      ? [
          {
            key: "charts",
            label: "Charts",
            icon: StockOutlined,
            children: stockLoading ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <ChartsTab
                selectedStock={selectedStock}
                baseCurrency={baseCurrency}
              />
            ),
          },
        ]
      : []),
    {
      key: "financials",
      label: "Financials",
      icon: BarChartOutlined,
      children: stockLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <FinancialsTab
          selectedStock={selectedStock}
          sectorPeers={sectorPeers}
          baseCurrency={baseCurrency}
        />
      ),
    },
    {
      key: "peers",
      label: "Peers",
      icon: TeamOutlined,
      children: stockLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <PeersTab
          sectorPeers={sectorPeers}
          baseCurrency={baseCurrency}
          selectedStock={selectedStock}
        />
      ),
    },
  ];

  const items: TabsProps["items"] = tabConfigs.map(
    ({ key, label, icon: Icon, children }) => ({
      key,
      label,
      icon: <Icon />,
      children,
    }),
  );

  return (
    <div className="w-full shadow-sm p-2">
      <Tabs
        className="vi-ssa-main-tabs"
        activeKey={activeTab}
        items={items}
        onChange={onTabChange}
        size="middle"
        tabBarStyle={{
          marginBottom: 0,
          paddingTop: 6,
          paddingBottom: 6,
          position: "sticky", // make the main tab bar sticky too
          top: 0,
          zIndex: 20,
          // Was a flat, fully opaque var(--vi-card-bg) — a wide band
          // sitting right where the page's own ambient accent-glow wash
          // is strongest (see .vi-ssa's background), so it blocked most
          // of it from ever showing. Glass surface fill + blur instead,
          // same "stays legible over scrolled content without being a
          // solid slab" treatment DynamicGraphs' sticky bars use.
          background: "var(--vi-glass-surface-fill)",
          backdropFilter: "var(--vi-glass-blur-surface)",
          WebkitBackdropFilter: "var(--vi-glass-blur-surface)",
        }}
      />
    </div>
  );
};

export default StockAnalysisTabs;
